import { useState } from "react";
import { datasetLabel } from "../helpers/datasetLabels";

const STAGE_CLOSED = 'closed';
const STAGE_SYNC_CONFIRM = 'sync-confirm';
const STAGE_UNSYNC_WARNING = 'unsync-warning';

const labelFor = (dataset, datasetContactNames) => {
  const name = datasetContactNames?.[dataset];
  return name ? `${name} (${datasetLabel(dataset)})` : datasetLabel(dataset);
};

export function useLinkedinCrossDatasetConfirm(currentDataset) {
  const [stage, setStage] = useState(STAGE_CLOSED);
  const [otherContactLabels, setOtherContactLabels] = useState([]);
  const [currentContactLabel, setCurrentContactLabel] = useState('');
  const [resolve, setResolve] = useState(null);

  // On an import match the url arrived from the person being linked to, so there is nothing to
  // propagate and nothing to ask about. Only while it is still that url: an accepted match stays
  // on the form for the rest of the session, so editing the url afterwards has to be confirmed
  // like any other change - the save still writes it onto the linked person.
  const confirmLinkedinChange = (
    datasets, originalValue, newValue, datasetContactNames,
    {importingMatch = false, importedValue = null} = {}
  ) => {
    const changed = (originalValue || '') !== (newValue || '');
    const importing = importingMatch && (importedValue || '') === (newValue || '');
    const otherDatasets = (datasets || []).filter((dataset) => dataset !== currentDataset);

    if (!changed || importing || otherDatasets.length === 0) {
      return Promise.resolve(null);
    }

    setCurrentContactLabel(labelFor(currentDataset, datasetContactNames));
    setOtherContactLabels(otherDatasets.map((dataset) => labelFor(dataset, datasetContactNames)));

    return new Promise((res) => {
      setResolve(() => res);
      setStage(STAGE_SYNC_CONFIRM);
    });
  };

  const handleSyncConfirm = () => {
    resolve?.('sync');
    setStage(STAGE_CLOSED);
    setResolve(null);
  };

  const handleSyncDecline = () => {
    setStage(STAGE_UNSYNC_WARNING);
  };

  const handleSyncAbort = () => {
    resolve?.('abort');
    setStage(STAGE_CLOSED);
    setResolve(null);
  };

  const handleUnsyncConfirm = () => {
    resolve?.('unsync');
    setStage(STAGE_CLOSED);
    setResolve(null);
  };

  const handleUnsyncDecline = () => {
    setStage(STAGE_SYNC_CONFIRM);
  };

  return {
    linkedinConfirmDialogProps: {
      open: stage === STAGE_SYNC_CONFIRM,
      currentContactLabel,
      otherContactLabels,
      onConfirm: handleSyncConfirm,
      onCancel: handleSyncDecline,
      onClose: handleSyncAbort,
    },
    linkedinUnsyncWarningDialogProps: {
      open: stage === STAGE_UNSYNC_WARNING,
      currentDatasetLabel: datasetLabel(currentDataset),
      onConfirm: handleUnsyncConfirm,
      onCancel: handleUnsyncDecline,
    },
    confirmLinkedinChange,
  };
}

export default useLinkedinCrossDatasetConfirm;